import { Helmet } from "react-helmet-async";

// Canonical origin — canonical URLs, OG, and JSON-LD all reference this.
// Update if the production domain ever changes.
export const SITE_ORIGIN = "https://nylaairwater.earth";
export const SITE_NAME = "Nyla Air Water";
const DEFAULT_TITLE = "Nyla Air Water — Where Nature Meets Purity";
const DEFAULT_DESCRIPTION =
  "Nyla Air Water — premium bottled water gathered from atmospheric moisture. Pure, mineral-enriched, sustainable hydration. Born from air. Perfected by nature. Hyderabad, India.";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/og-image.png`;

/**
 * Per-page SEO. Every prop is optional; sensible site-wide defaults are used
 * when a page does not provide one.
 *
 *  - `title`      → suffixed with " — Nyla Air Water" unless you pass `absoluteTitle`
 *  - `path`       → the canonical path, e.g. "/products" (leading slash)
 *  - `image`      → absolute URL to a 1200×630 share image
 *  - `type`       → OG type: "website" | "article" | "product"
 *  - `keywords`   → optional string
 *  - `jsonLd`     → optional object or array of structured-data objects
 *  - `noindex`    → set true for /admin, drafts, etc.
 *  - `article`    → { publishedTime, modifiedTime, author, section, tags }
 */
export const SEO = ({
  title,
  absoluteTitle = false,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  keywords,
  jsonLd,
  noindex = false,
  article,
}) => {
  const fullTitle = title
    ? absoluteTitle
      ? title
      : `${title} — ${SITE_NAME}`
    : DEFAULT_TITLE;
  const canonical = `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      {/* Article-specific OG tags */}
      {type === "article" && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {type === "article" && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {type === "article" && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {type === "article" && article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      {type === "article" &&
        Array.isArray(article?.tags) &&
        article.tags.map((t) => <meta key={t} property="article:tag" content={t} />)}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@nylaairwater" />
      <meta name="twitter:creator" content="@nylaairwater" />

      {ldArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};
